/**
 * `<SourceManagementSurface>` — the Source Management surface root (frontend.md §1.3
 * Tier-1 full build, §2.1).
 *
 * frontend.md §1.3 makes this a full Tier-1 build — "needed to know ingest is alive".
 * Composes `<JoplinSyncPanel>` (connection + sync status + the D-007 write-back state
 * mirror), `<MarkdownImportPanel>` (the import trigger), and `<PlaudSourcePanel>` (the
 * disabled v1.5 placeholder).
 *
 * Like Corpus Health, the polling panels are passed `active` = "this surface is the
 * visible route" so polling pauses on navigation away (frontend.md §1.5).
 */

import { useLocation } from 'react-router-dom';
import { surfaceForPath } from '@/routes';
import { SurfaceLayout } from '@/components';
import { JoplinSyncPanel } from './JoplinSyncPanel';
import { MarkdownImportPanel } from './MarkdownImportPanel';
import { PlaudSourcePanel } from './PlaudSourcePanel';

interface SourceManagementSurfaceProps {
  /**
   * When `true`, this surface is rendered EMBEDDED inside another surface (the
   * `<SettingsSurface>` "Sources" sub-tab post-IA-contraction 2026-05-16) — skip the
   * outer `<SurfaceLayout>` so we don't render a second header inside the host.
   */
  embedded?: boolean;
  /**
   * Override the route-derived "this is the active route" flag — required when embedded
   * (the route is `/settings`, not `/sources`).
   */
  active?: boolean;
}

export function SourceManagementSurface({
  embedded = false,
  active: activeOverride,
}: SourceManagementSurfaceProps = {}): JSX.Element {
  const location = useLocation();
  const active = activeOverride ?? surfaceForPath(location.pathname) === 'sources';

  const body = (
    <>
      <JoplinSyncPanel active={active} />
      <MarkdownImportPanel active={active} />
      <PlaudSourcePanel />
    </>
  );

  if (embedded) return <div className="surface-embedded">{body}</div>;
  return <SurfaceLayout title="Source Management">{body}</SurfaceLayout>;
}
